import { useCallback, useMemo, useState } from 'react';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { unwrapResult } from '@reduxjs/toolkit';

import { Branch } from 'src/types/branch';
import useUserInfo from '../user/use-user-info';
import { taptapOptionsBranchReleased } from 'src/redux/slices/taptap-customer';

const { actions: branchActions, selectors: branchSelectors } = slices.branch;

export const useBranchInfo = () => {
  const dispatch = useAppDispatch();
  const branches = useAppSelector(branchSelectors.selectBranchesList);
  const { userBranchOptions } = useUserInfo();

  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  const [selectedBranches, setSelectedBranches] = useState<Branch[]>([]);
  const [selectedBranchIDs, setSelectedBranchIDs] = useState<number[]>([]);
  const [branchInputValue, setBranchInputValue] = useState<string>('');

  const defaultValueBranch = useMemo(() => {
    const branchIds: number[] = userBranchOptions.map(
      (branch) => branch?.value
    );
    return branchIds.length > 0 ? [branchIds[0]] : [];
  }, [userBranchOptions]);

  const defaultBranch = useMemo(() => {
    return userBranchOptions.length > 0 ? userBranchOptions[0] : null;
  }, [userBranchOptions]);

  const branchIDToName = useCallback(
    (val?: number) => {
      const branchName =
        userBranchOptions.find((i) => i.value === val)?.name || '';
      return branchName;
    },
    [userBranchOptions]
  );

  const branchNameToID = useCallback(
    (val: string) => {
      return userBranchOptions.find((i) => i.name === val)?.value || -1;
    },
    [userBranchOptions]
  );

  const getBranches = useCallback(
    async (keyword: string = '') => {
      await dispatch(branchActions.getBranchesThunk({ keyword, limit: 50 }));
    },
    [dispatch]
  );

  // eslint-disable-next-line no-undef
  const getBranchDetails = async (branch: Partial<Branch>) => {
    return unwrapResult(
      await dispatch(branchActions.getBranchDetailsThunk(branch))
    );
  };

  // As of July 15, 2025. Here are production branches
  // 1	Earnshaw - Manila
  // 2	LPC - Alabang Zapote
  // 3	PGDX
  // 4	PC Worth Experience (QC) - Main
  const branchPONickNameViaID = (branchID?: number) => {
    switch (branchID) {
      case 1:
        return 'SAMPALOC';
      case 2:
        return 'LPC';
      case 3:
        return 'PGDX';
      case 4:
        return 'QC';
      default:
        return `${branchIDToName(branchID) || ''}`;
    }
  };

  const getTapTapBranchReleasedID = (taptapBranchReleasedName?: string) => {
    return taptapOptionsBranchReleased.find(
      (branch) => branch.value === taptapBranchReleasedName
    )?.branch_id;
  };

  return {
    branches,
    defaultBranch,
    defaultValueBranch,
    branchIDToName,
    branchNameToID,
    selectedBranch,
    setSelectedBranch,
    selectedBranchIDs,
    setSelectedBranchIDs,
    branchInputValue,
    setBranchInputValue,
    getBranches,
    selectedBranches,
    setSelectedBranches,
    getBranchDetails,
    branchPONickNameViaID,
    userBranchOptions,
    getTapTapBranchReleasedID
  };
};
